import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, MapPin, Phone, Mail, Clock, Star, Shield, ChevronLeft, Stethoscope, Activity, Heart, PenSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { getMe } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const ratingLabels = { 1: 'Very Poor', 2: 'Poor', 3: 'Okay', 4: 'Good', 5: 'Excellent' };

const StarRatingInput = ({ value, onChange, label }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-[#262626] mb-2">{label}</label>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button key={star} type="button" onClick={() => onChange(star)} className="p-1 hover:scale-110 transition-transform">
          <Star className={`h-7 w-7 ${star <= value ? 'fill-[#2DD4BF] text-[#2DD4BF]' : 'text-slate-300 hover:text-[#2DD4BF]/50'}`} />
        </button>
      ))}
      {value > 0 && <span className="ml-3 text-sm font-medium text-[#4B5563]">{ratingLabels[value]}</span>}
    </div>
  </div>
);

export default function FacilityProfilePage() {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [facility, setFacility] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewData, setReviewData] = useState({
    star_rating: 0, cleanliness: 0, wait_time: 0, staff_friendliness: 0, equipment_quality: 0, text_review: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      try { const r = await getMe(); setUser(r.data); } catch { setUser(null); }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [facRes, revRes] = await Promise.all([
          api.get(`/facilities/${facilityId}`),
          api.get(`/facility-reviews/${facilityId}`)
        ]);
        setFacility(facRes.data);
        setReviews(revRes.data || []);
      } catch {
        toast.error('Facility not found');
        navigate('/facilities');
      } finally { setLoading(false); }
    };
    load();
  }, [facilityId, navigate]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewData.star_rating) { toast.error('Please provide an overall rating'); return; }
    setSubmittingReview(true);
    try {
      await api.post('/facility-reviews', { ...reviewData, facility_id: facilityId });
      toast.success('Review submitted!');
      setShowReviewModal(false);
      setReviewData({ star_rating: 0, cleanliness: 0, wait_time: 0, staff_friendliness: 0, equipment_quality: 0, text_review: '' });
      // Reload reviews and facility
      const [facRes, revRes] = await Promise.all([
        api.get(`/facilities/${facilityId}`),
        api.get(`/facility-reviews/${facilityId}`)
      ]);
      setFacility(facRes.data);
      setReviews(revRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Please sign in to submit a review');
    }
    setSubmittingReview(false);
  };

  const getTypeColor = (type) => {
    const colors = {
      'Clinic': 'bg-blue-100 text-blue-700', 'Dental Clinic': 'bg-purple-100 text-purple-700',
      'Optical Clinic': 'bg-teal-100 text-teal-700', 'Hospital': 'bg-emerald-100 text-emerald-700',
      'Multi-specialty Hospital': 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  if (!facility) return null;

  const hasHours = facility.opening_hours && Object.keys(facility.opening_hours).length > 0;
  const hasSpecialties = facility.specialties_available && Object.keys(facility.specialties_available).length > 0;
  const hourCategories = [
    { key: 'weekdays', label: 'Mon - Fri' }, { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }, { key: 'public_holidays', label: 'Public Holidays' },
    { key: 'all', label: 'All Days' }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      {/* Hero Banner */}
      <div className="relative h-64 bg-gradient-to-br from-[#0F2A44] to-[#1a3d5c] overflow-hidden">
        {facility.facility_images?.length > 0 && (
          <img src={facility.facility_images[0]} alt={facility.facility_name} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-end pb-8">
          <button onClick={() => navigate('/facilities')} className="text-white/60 hover:text-white flex items-center gap-1 text-sm mb-4 transition-colors" data-testid="back-to-facilities">
            <ChevronLeft className="h-4 w-4" /> Back to Facilities
          </button>
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getTypeColor(facility.facility_type)}`}>{facility.facility_type}</span>
            {facility.hospital_level && <span className="px-3 py-1 text-xs font-semibold rounded-full bg-white/20 text-white">{facility.hospital_level}</span>}
            {facility.kmpdc_verified && <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-300"><Shield className="h-3 w-3 inline mr-1" /> KMPDC Verified</span>}
          </div>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-white" data-testid="facility-name">{facility.facility_name}</h1>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <Star className="h-5 w-5 fill-[#2DD4BF] text-[#2DD4BF]" />
              <span className="font-space font-bold text-white text-lg">{(facility.average_rating || 0).toFixed(1)}</span>
              <span className="text-white/60 text-sm">({facility.total_reviews || 0} reviews)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {facility.bio && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-bio">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-3">About</h2>
                <p className="font-public text-[#4B5563] leading-relaxed whitespace-pre-wrap">{facility.bio}</p>
              </div>
            )}

            {facility.signature_services?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-signature-services">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4 flex items-center gap-2"><Heart className="h-5 w-5 text-[#2DD4BF]" /> Signature Services</h2>
                <div className="flex flex-wrap gap-3">
                  {facility.signature_services.map((svc, idx) => (
                    <span key={idx} className="px-4 py-2 bg-[#2DD4BF]/10 text-[#0F2A44] rounded-lg font-medium text-sm border border-[#2DD4BF]/20">{svc}</span>
                  ))}
                </div>
              </div>
            )}

            {hasSpecialties && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-specialties">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4 flex items-center gap-2"><Stethoscope className="h-5 w-5 text-[#4A90E2]" /> Specialties Available</h2>
                <div className="space-y-3">
                  {Object.entries(facility.specialties_available).map(([spec, days]) => (
                    <div key={spec} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium text-[#0F2A44] text-sm">{spec}</span>
                      <div className="flex gap-1">
                        {Array.isArray(days) ? days.map(day => (
                          <span key={day} className="px-2 py-0.5 bg-[#4A90E2]/10 text-[#4A90E2] text-xs rounded">{day.slice(0, 3)}</span>
                        )) : <span className="text-xs text-[#4B5563]">All days</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {facility.services_available?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-services">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4 flex items-center gap-2"><Activity className="h-5 w-5 text-[#2DD4BF]" /> Services</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {facility.services_available.map((svc, idx) => (
                    <span key={idx} className="px-3 py-2 bg-slate-50 text-[#4B5563] text-sm rounded-lg font-public">{svc}</span>
                  ))}
                </div>
              </div>
            )}

            {facility.imaging_modalities?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-imaging">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">Imaging & Diagnostics</h2>
                <div className="flex flex-wrap gap-2">
                  {facility.imaging_modalities.map((mod, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-purple-50 text-purple-700 text-sm rounded-lg font-public border border-purple-100">{mod}</span>
                  ))}
                </div>
              </div>
            )}

            {facility.insurances_accepted?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-insurances">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">Insurances Accepted</h2>
                <div className="flex flex-wrap gap-2">
                  {facility.insurances_accepted.map((ins, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-slate-100 text-[#4B5563] text-sm rounded-full font-public">{ins}</span>
                  ))}
                </div>
              </div>
            )}

            {facility.facility_images?.length > 1 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-gallery">
                <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {facility.facility_images.map((img, idx) => (
                    <img key={idx} src={img} alt={`${facility.facility_name} ${idx + 1}`} className="w-full h-40 object-cover rounded-lg" />
                  ))}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-reviews-section">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-manrope font-bold text-xl text-[#0F2A44] flex items-center gap-2">
                    <Star className="h-5 w-5 text-[#2DD4BF]" /> Reviews & Ratings
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-space font-bold text-2xl text-[#0F2A44]">{(facility.average_rating || 0).toFixed(1)}</span>
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`h-4 w-4 ${s <= Math.round(facility.average_rating || 0) ? 'fill-[#2DD4BF] text-[#2DD4BF]' : 'text-slate-300'}`} />
                      ))}
                    </div>
                    <span className="text-sm text-[#4B5563]">({facility.total_reviews || 0} reviews)</span>
                  </div>
                </div>
                <Button onClick={() => user ? setShowReviewModal(true) : toast.error('Please sign in to write a review')} data-testid="facility-write-review-btn" className="bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white">
                  <PenSquare className="h-4 w-4 mr-2" /> Write Review
                </Button>
              </div>

              {reviews.length === 0 ? (
                <p className="text-center text-[#4B5563] py-6">No reviews yet. Be the first to share your experience!</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="border-b border-slate-100 pb-4 last:border-0" data-testid={`facility-review-${review.review_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0F2A44] flex items-center justify-center">
                            <span className="text-white font-manrope font-bold">{(review.user_name || 'A').charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-manrope font-semibold text-[#0F2A44] text-sm">{review.user_name}</p>
                            <span className="text-xs text-[#4B5563]">{new Date(review.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`h-4 w-4 ${i < review.star_rating ? 'fill-[#2DD4BF] text-[#2DD4BF]' : 'text-slate-300'}`} />
                          ))}
                        </div>
                      </div>
                      {review.text_review && <p className="font-public text-sm text-[#4B5563] mt-2">{review.text_review}</p>}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div className="text-xs"><span className="text-[#4B5563]">Cleanliness: </span><span className="font-space font-medium text-[#2DD4BF]">{review.cleanliness}/5</span></div>
                        <div className="text-xs"><span className="text-[#4B5563]">Wait Time: </span><span className="font-space font-medium text-[#2DD4BF]">{review.wait_time}/5</span></div>
                        <div className="text-xs"><span className="text-[#4B5563]">Staff: </span><span className="font-space font-medium text-[#2DD4BF]">{review.staff_friendliness}/5</span></div>
                        <div className="text-xs"><span className="text-[#4B5563]">Equipment: </span><span className="font-space font-medium text-[#2DD4BF]">{review.equipment_quality}/5</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-contact-card">
              <h2 className="font-manrope font-bold text-lg text-[#0F2A44] mb-4">Contact Information</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#4A90E2] flex-shrink-0 mt-0.5" />
                  <div><p className="text-sm text-[#4B5563]">{facility.location}</p><p className="text-xs text-slate-400">{facility.region}</p></div>
                </div>
                {facility.contact_phone && (
                  <a href={`tel:${facility.contact_phone}`} className="flex items-center gap-3 text-sm text-[#4B5563] hover:text-[#0F2A44]">
                    <Phone className="h-5 w-5 text-[#4A90E2]" /><span>{facility.contact_phone}</span>
                  </a>
                )}
                {facility.contact_email && (
                  <a href={`mailto:${facility.contact_email}`} className="flex items-center gap-3 text-sm text-[#4B5563] hover:text-[#0F2A44]">
                    <Mail className="h-5 w-5 text-[#4A90E2]" /><span>{facility.contact_email}</span>
                  </a>
                )}
              </div>
              {facility.pin_location?.lat && facility.pin_location?.lng && (
                <a href={`https://www.google.com/maps?q=${facility.pin_location.lat},${facility.pin_location.lng}`} target="_blank" rel="noopener noreferrer" className="mt-4 block" data-testid="facility-map-link">
                  <Button variant="outline" className="w-full border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2]/5"><MapPin className="h-4 w-4 mr-2" /> View on Map</Button>
                </a>
              )}
            </div>

            {facility.contact_person_name && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-contact-person">
                <h2 className="font-manrope font-bold text-lg text-[#0F2A44] mb-3">Contact Person</h2>
                <p className="font-medium text-[#0F2A44] text-sm">{facility.contact_person_name}</p>
                {facility.contact_person_designation && <p className="text-xs text-[#4B5563] mt-1">{facility.contact_person_designation}</p>}
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-hours">
              <h2 className="font-manrope font-bold text-lg text-[#0F2A44] mb-4 flex items-center gap-2"><Clock className="h-5 w-5 text-[#4A90E2]" /> Working Hours</h2>
              {facility.is_24_7 ? (
                <div className="text-center py-4"><span className="inline-block px-6 py-3 bg-green-50 text-green-700 font-semibold rounded-full text-lg border border-green-200">Open 24/7</span></div>
              ) : hasHours ? (
                <div className="space-y-2">
                  {hourCategories.map(({ key, label }) => {
                    const val = facility.opening_hours?.[key];
                    if (!val) return null;
                    const isClosed = val === 'Closed';
                    return (
                      <div key={key} className="flex justify-between items-center text-sm">
                        <span className="text-[#4B5563]">{label}</span>
                        <span className={`font-space font-medium ${isClosed ? 'text-red-400' : 'text-[#0F2A44]'}`}>{val}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-slate-400 text-center">Contact for hours</p>}
            </div>

            {facility.kmpdc_registration && (
              <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="facility-kmpdc">
                <h2 className="font-manrope font-bold text-lg text-[#0F2A44] mb-3 flex items-center gap-2"><Shield className="h-5 w-5 text-[#4A90E2]" /> KMPDC Registration</h2>
                <p className="font-space text-sm text-[#4B5563]">{facility.kmpdc_registration}</p>
                <span className={`mt-2 inline-block px-3 py-1 text-xs rounded-full font-medium ${facility.kmpdc_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {facility.kmpdc_verified ? 'Verified' : 'Pending Verification'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope text-2xl text-[#0F2A44]">Review {facility.facility_name}</DialogTitle>
            <DialogDescription className="font-public text-[#4B5563]">Share your experience at this facility</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReviewSubmit} className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <StarRatingInput label="Overall Rating *" value={reviewData.star_rating} onChange={(val) => setReviewData({ ...reviewData, star_rating: val })} />
            </div>
            <div className="space-y-4">
              <p className="text-sm font-medium text-[#262626]">Rate the following aspects:</p>
              <StarRatingInput label="Cleanliness *" value={reviewData.cleanliness} onChange={(val) => setReviewData({ ...reviewData, cleanliness: val })} />
              <StarRatingInput label="Wait Time *" value={reviewData.wait_time} onChange={(val) => setReviewData({ ...reviewData, wait_time: val })} />
              <StarRatingInput label="Staff Friendliness *" value={reviewData.staff_friendliness} onChange={(val) => setReviewData({ ...reviewData, staff_friendliness: val })} />
              <StarRatingInput label="Equipment Quality *" value={reviewData.equipment_quality} onChange={(val) => setReviewData({ ...reviewData, equipment_quality: val })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-2">Your Review (Optional)</label>
              <Textarea placeholder="Share more details about your experience..." value={reviewData.text_review} onChange={(e) => setReviewData({ ...reviewData, text_review: e.target.value })} className="min-h-[100px]" data-testid="facility-review-text-input" />
            </div>
            <Button type="submit" disabled={submittingReview} data-testid="facility-review-submit-btn" className="w-full bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-manrope font-semibold">
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
